import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import {
  getGuestAgentLookupIdentifiers,
  invalidateGuestAgentLookupQueries,
} from '~/features/chatbot/utils'

const REFETCH_DEBOUNCE_MS = 150

type TParams = {
  agentId?: string
  legacyChatbotId?: string
  friendlyUrl?: string
  pathname: string
}

const useRefetchGuestAgentOnWindowFocus = ({
  agentId,
  legacyChatbotId,
  friendlyUrl,
  pathname,
}: TParams) => {
  const queryClient = useQueryClient()

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | undefined

    const scheduleInvalidate = () => {
      if (document.visibilityState === 'hidden') return

      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        invalidateGuestAgentLookupQueries(
          queryClient,
          getGuestAgentLookupIdentifiers({
            agentId,
            legacyChatbotId,
            friendlyUrl,
            pathname,
          }),
        ).catch(() => undefined)
      }, REFETCH_DEBOUNCE_MS)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      scheduleInvalidate()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', scheduleInvalidate)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', scheduleInvalidate)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [queryClient, agentId, legacyChatbotId, friendlyUrl, pathname])
}

export default useRefetchGuestAgentOnWindowFocus
